'use client';

import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Avatar, Box, Typography, useTheme } from '@mui/material';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import Link from 'next/link';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { ImageUrl } from '@/components/Utility/StrapiImage';
import { ComponentBlocksTeam, TeamMember } from '@/types';

interface TeamProps {
  teamContent: BlocksContent;
  teamMembers: TeamMember[];
  teams: ComponentBlocksTeam[];
}

export default function Team(props: TeamProps) {
  return (
    <CustomBackground
      backgroundOptions={{
        BackgroundColor: 'purple-500',
        BackgroundOrbs: 'white-9-left',
      }}
      displayOptions={{
        PaddingTop: 'md',
        PaddingBottom: 'md',
        PaddingInline: 'sm',
        CenterAlignTitles: true,
        CenterAlignParagraphs: true,
      }}
    >
      <GlobalBlockRenderer content={props.teamContent || []} />
      <Box pt={1}>
        <TeamLayout name="Leadership" members={props.teamMembers ?? []} />

        {props.teams &&
          props.teams.map((team) => (
            <TeamLayout
              key={`${team.id}-${team.Name}`}
              name={team.Name}
              members={(team?.TeamMembers || []) as TeamMember[]}
            />
          ))}
      </Box>
    </CustomBackground>
  );
}

function TeamLayout({ name, members }: { members: TeamMember[]; name: string }) {
  return (
    <Box
      sx={{
        marginBottom: 4,
      }}
    >
      <Typography
        variant="h2"
        component="h2"
        sx={{
          my: 2,
          mb: 0,
        }}
      >
        <strong>{name}</strong>
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
        }}
      >
        {members &&
          members
            .map((member) => member as TeamMember)
            .map((member) => (
              <TeamMemberCard
                key={member.documentId}
                avatarSize={150}
                backgroundColor="#ffffff"
                member={member}
                padding={3}
              />
            ))}
      </Box>
    </Box>
  );
}

function TeamMemberCard({
  member,
  backgroundColor,
  avatarSize,
  padding,
}: {
  member: TeamMember;
  backgroundColor: string;
  avatarSize: number;
  padding: number;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: '3.125rem',
        border: `0.375rem solid ${theme.palette.purple[400]}`,
        p: padding,
        pb: padding / 4,
        my: 2,
        mb: 1,
        backgroundColor: backgroundColor,
        mr: { xs: 2 },
        minWidth: '12.5rem',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {member.Avatar && (
          <Avatar
            src={ImageUrl({ image: member.Avatar, size: 'small' }) ?? undefined}
            sx={{
              border: `0.375rem solid ${theme.palette.purple[400]}`,
              width: { xs: Math.floor(avatarSize / 2), md: avatarSize },
              height: { xs: Math.floor(avatarSize / 2), md: avatarSize },
            }}
          />
        )}

        {!member.Avatar && (
          <Avatar
            src=""
            sx={{
              border: `0.375rem solid ${theme.palette.purple[400]}`,
              width: { xs: Math.floor(avatarSize / 2), md: avatarSize },
              height: { xs: Math.floor(avatarSize / 2), md: avatarSize },
            }}
          />
        )}
      </Box>
      <Box>
        <Typography fontSize={32} color={theme.palette.brand.primary} mt={2} textAlign="center">
          {member.Name}
        </Typography>
        <Typography color={theme.palette.text.primary} textAlign="center">
          {member.Role}
        </Typography>

        <Typography
          color={theme.palette.text.primary}
          fontSize={theme.typography.small.fontSize}
          mt={2}
        >
          {member.Bio}
        </Typography>

        <Typography textAlign="center" fontSize={16} mt={4}>
          {member.LinkedInUrl && (
            <Link href={member.LinkedInUrl} rel="external">
              <LinkedInIcon htmlColor={theme.palette.purple[500]} fontSize="large" sx={{ mr: 3 }} />
            </Link>
          )}
          {member.Email && (
            <Link href={`mailto:${member.Email}`} rel="external">
              <EmailIcon htmlColor={theme.palette.purple[500]} fontSize="large" />
            </Link>
          )}
        </Typography>
      </Box>
    </Box>
  );
}
